import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { FormGroup } from "@angular/forms";
import { InvoiceConfiguration } from "@data/schemas/invoice-configuration/invoice-configuration";
import { Observable, Subject } from "rxjs";
import { CustomSegmentService } from "@core/services/custom-segment.service";
import { v4 as uuidv4 } from "uuid";
import { InvoiceConfigSteps } from "@modules/invoicing/pages/invoice/invoice-configuration-wizard/types/invoice-config-steps";
import { IWizardInvoiceConfigurationBasicInformation } from "@modules/invoicing/pages/invoice/invoice-configuration-wizard/types/wizard-invoice-configuration-basic-information.interface";
import { IWizardInvoiceConfigurationDigitalCertificate } from "@modules/invoicing/pages/invoice/invoice-configuration-wizard/types/wizard-invoice-configuration-digital-certificate.interface";
import { IWizardInvoiceConfigurationTaxesData } from "@modules/invoicing/pages/invoice/invoice-configuration-wizard/types/wizard-invoice-configuration-taxes-data.interface";

export type WizardTrackingEvent =
	| "click_configure"
	| "step_tax_data"
	| "emission_environment"
	| "step_certificate_digital"
	| "step_taxes_group"
	| "step_summary"
	| "summary_finish";

interface FormValues {
	[key: string]: unknown;
}

@Injectable({
	providedIn: "root"
})
export class WizardInvoiceConfigurationService {
	readonly INVOICE_CONFIGURATION_WIZARD_ENDPOINT =
		"wizard/invoice-configuration";
	readonly PATH_STEP_INFORMATION = "step-information";

	private updateSubject = new Subject<void>();

	private interactionTrackingId: string = uuidv4();
	private previousFormValues: FormValues = {};

	update$ = this.updateSubject.asObservable();

	constructor(
		private http: HttpClient,
		private segment: CustomSegmentService
	) {}

	emitUpdate(): void {
		this.updateSubject.next();
	}

	sendInvoiceConfiguration(
		invoiceConfiguration: InvoiceConfiguration
	): Observable<void> {
		return this.http.post<void>(
			this.INVOICE_CONFIGURATION_WIZARD_ENDPOINT,
			invoiceConfiguration,
			{ withCredentials: true }
		);
	}

	public trackWizard(
		event: WizardTrackingEvent,
		properties?: { [k: string]: unknown },
		form?: FormGroup | null
	): void {
		this.interactionTrackingId = this.getInteractionTrackingId(properties);

		const flattenedDiff = form ? this.getTrackedFieldChanges(form) : {};
		const groupedDiff = this.groupByParentKey(flattenedDiff);

		const finalPayload = {
			table: "track_configuration_nfe",
			screen: "wizard",
			action: `base_erp_invoice_configuration_${event}`,
			properties: {
				...(properties || {}),
				interaction_id: this.interactionTrackingId,
				trackedFieldChanges: groupedDiff
			}
		};

		this.segment.track(finalPayload);
	}

	private getInteractionTrackingId(properties?: {
		[k: string]: unknown;
	}): string {
		return (properties?.interaction_id ||
			this.interactionTrackingId ||
			uuidv4()) as string;
	}

	private getTrackedFieldChanges(form: FormGroup): Record<string, unknown> {
		const currentValues = form.getRawValue() as FormValues;

		const flattenedCurrent = this.flattenObject(currentValues);
		const flattenedPrevious = this.flattenObject(this.previousFormValues);

		const changes: Record<string, unknown> = {};

		Object.keys(flattenedCurrent).forEach((key) => {
			const currentVal = flattenedCurrent[key];
			const prevVal = flattenedPrevious[key];

			if (
				JSON.stringify(currentVal) !== JSON.stringify(prevVal) ||
				isEmptyValue(currentVal)
			) {
				changes[key] = currentVal;
			}
		});

		this.previousFormValues = structuredClone(currentValues);

		return changes;
	}

	private groupByParentKey(
		changes: Record<string, unknown>
	): Record<string, unknown> {
		const result: Record<string, Record<string, unknown>> = {};

		for (const flattenedKey of Object.keys(changes)) {
			const segments = flattenedKey.split(".");
			let pointer: Record<string, Record<string, unknown>> = result;

			for (let i = 0; i < segments.length - 1; i++) {
				const seg = segments[i];
				if (!pointer[seg]) {
					pointer[seg] = {};
				}
				pointer = pointer[seg] as Record<
					string,
					Record<string, unknown>
				>;
			}

			const lastSegment = segments[segments.length - 1];
			pointer[lastSegment] = changes[flattenedKey] as Record<
				string,
				unknown
			>;
		}

		return result;
	}

	private isNonObject(value: unknown): boolean {
		return !value || typeof value !== "object";
	}

	private flattenObject(
		obj: Record<string, unknown>,
		parentKey = ""
	): Record<string, unknown> {
		const result: Record<string, unknown> = {};

		if (this.isNonObject(obj)) {
			return result;
		}

		for (const key of Object.keys(obj)) {
			const fullKey = parentKey ? `${parentKey}.${key}` : key;
			const value = obj[key];

			if (
				!this.isNonObject(value) &&
				!Array.isArray(value) &&
				!(value instanceof Date)
			) {
				Object.assign(
					result,
					this.flattenObject(
						value as Record<string, unknown>,
						fullKey
					)
				);
			} else {
				result[fullKey] = value;
			}
		}

		return result;
	}

	public findStepInformation(step: InvoiceConfigSteps) {
		return this.http.get<
			| IWizardInvoiceConfigurationBasicInformation
			| IWizardInvoiceConfigurationDigitalCertificate
			| IWizardInvoiceConfigurationTaxesData
		>(
			`${this.INVOICE_CONFIGURATION_WIZARD_ENDPOINT}/${this.PATH_STEP_INFORMATION}/${step}`,
			{ withCredentials: true }
		);
	}
}

function isEmptyValue(value: unknown): boolean {
	if (value === null || value === undefined || value === "") {
		return true;
	}

	if (typeof value === "string" && value.trim().length === 0) {
		return true;
	}

	if (Array.isArray(value) && value.length === 0) {
		return true;
	}

	return false;
}
